import React from 'react';
import styled from 'styled-components';
import { Card } from 'antd';
import colors from 'Styles/Colors';
import moment from 'moment';

const MiscChargesCard = ({ data }) => {
  const miscCharges = data?.miscChargesSummary || [];
  const totalCharges = miscCharges.length;
  const totalAmount = miscCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <MiscCard>
      <Card title="Misc Charges" className="dashboard-card">
        <div className="charges-overview">
          <div className="main-stats">
            <div className="stat-item primary">
              <div className="stat-number">{totalCharges}</div>
              <div className="stat-label">Total Misc Charges</div>
            </div>
            <div className="stat-item secondary">
              <div className="stat-number">{formatCurrency(totalAmount)}</div>
              <div className="stat-label">Total Amount Collected</div>
            </div>
          </div>
        </div>

        {totalCharges > 0 && (
          <div className="charges-breakdown">
            <div className="breakdown-header">Recent Charges</div>
            <div className="charges-list">
              {miscCharges.slice(0, 3).map((charge, index) => (
                <div key={charge.id || index} className="charge-item">
                  <div className="charge-info">
                    <div className="charge-amount">{formatCurrency(charge.amount)}</div>
                    <div className="charge-date">{charge.createdAt ? moment(parseInt(charge.createdAt)).format('MM/DD/YYYY') : 'Unknown Date'}</div>
                  </div>
                  <div className="charge-notes" title={charge.notes || 'No notes'}>
                    {charge.notes || 'No notes'}
                  </div>
                </div>
              ))}
            </div>
            {totalCharges > 3 && <div className="more-charges">+{totalCharges - 3} more charges</div>}
          </div>
        )}

        {totalCharges === 0 && (
          <div className="no-charges">
            <div className="no-charges-text">No misc charges recorded</div>
          </div>
        )}
      </Card>
    </MiscCard>
  );
};

const MiscCard = styled.div`
  flex: 1;
  min-width: 280px;

  .dashboard-card {
    height: 100%;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
    }

    .ant-card-body {
      padding: 16px;
    }
  }

  .charges-overview {
    margin-bottom: 20px;
  }

  .main-stats {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .stat-item {
      text-align: center;
      padding: 16px;
      border-radius: 8px;

      &.primary {
        background: linear-gradient(135deg, ${colors.primary}15, ${colors.primary}05);
        border: 1px solid ${colors.primary}20;
      }

      &.secondary {
        background: linear-gradient(135deg, ${colors.secondary}15, ${colors.secondary}05);
        border: 1px solid ${colors.secondary}20;
      }

      .stat-number {
        font-size: 32px;
        font-weight: 700;
        color: ${colors.text.primary};
        line-height: 1;
        margin-bottom: 8px;
      }

      .stat-label {
        font-size: 12px;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        font-weight: 500;
        letter-spacing: 0.5px;
      }
    }
  }

  .charges-breakdown {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;

    .breakdown-header {
      font-size: 14px;
      font-weight: 600;
      color: ${colors.text.primary};
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .charges-list {
    .charge-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid ${colors.border.primary}50;

      &:last-child {
        border-bottom: none;
      }

      .charge-info {
        display: flex;
        flex-direction: column;

        .charge-amount {
          font-size: 16px;
          font-weight: 600;
          color: ${colors.text.primary};
        }

        .charge-date {
          font-size: 12px;
          color: ${colors.text.secondary};
        }
      }

      .charge-notes {
        flex: 1;
        text-align: right;
        font-size: 12px;
        color: ${colors.text.secondary};
        margin-left: 12px;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .more-charges {
    text-align: center;
    font-size: 12px;
    color: ${colors.text.secondary};
    margin-top: 12px;
    padding: 8px;
    background: ${colors.background.primary};
    border-radius: 4px;
  }

  .no-charges {
    text-align: center;
    padding: 40px 20px;

    .no-charges-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .no-charges-text {
      font-size: 14px;
      color: ${colors.text.secondary};
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;

    .main-stats {
      .stat-item {
        .stat-number {
          font-size: 28px;
        }
      }
    }

    .charges-list {
      .charge-item {
        flex-direction: column;
        align-items: flex-start;

        .charge-notes {
          text-align: left;
          margin-left: 0;
          margin-top: 4px;
          max-width: 100%;
        }
      }
    }
  }
`;

export default MiscChargesCard;
